import { createSelector } from 'reselect';

import mapCountryTaxTypes from '../utils/mapCountryTaxTypes';
import { byField } from '../../../utils/sorting';
import { encodeCountryTaxType } from '../../../utils/encodeDecodeCTT';
import { groupingFields } from '../constants';

import { formatValue, NumeralTypes } from '../../../shared/numeral.component';
import {
  checkAccessBasedOnAssignmentPermissions,
  assignmentTypes,
  assignmentPermissions,
} from '../../../shared/authorization';

import {
  isInCurrentPeriodSelector,
  isFunctionalCurrencySelectedSelector,
} from '../../../shared/store/selectors';

import { YES_FLAG } from '../../../shared/constants';

export const selectSlGroupTypes = createSelector(
  ({ reports }) => reports.effectiveTaxRate.groupTypes,
  groupTypes => groupTypes.map(({ name, value }) => ({
    label: name,
    value,
  }))
);

export const groupByOptionsSelector = createSelector(
  ({ reports }) => reports.effectiveTaxRate.groupByOptions,
  groupByOptions => groupByOptions.map(({ name, value }) => ({
    label: name,
    value,
  }))
);

export const selectSlGroupItems = createSelector(
  ({ reports }) => reports.effectiveTaxRate.groupItems,
  slGroupItems => [
    { value: 'ALL', label: 'All' },
    ...slGroupItems.map(({ groupItem, groupItemDescription }) => ({
      label: groupItemDescription,
      value: groupItem,
    })),
  ]
);

export const selectSlGroupType = state => state.reports.effectiveTaxRate.groupType;
export const selectSlGroupItem = state => state.reports.effectiveTaxRate.groupItem;
export const groupBySelector = state => state.reports.effectiveTaxRate.groupBy;
export const viewBySelector = state => state.reports.effectiveTaxRate.viewBy;

export const selectCountryTaxTypesForModal = createSelector(
  ({ reports }) => reports.effectiveTaxRate.manageCustomDriversModal.countryTaxTypes,
  countryTaxTypes => countryTaxTypes.map(({ countryTaxType, description, currencyCode }) => ({
    label: `${countryTaxType} - ${description}`,
    value: countryTaxType,
    currencyCode,
  })).sort(byField('label'))
);

export const selectETRGroups = createSelector(
  ({ reports }) => reports.effectiveTaxRate.ETRGroups,
  ETRGroups => ETRGroups.map(({ tagName, description }) => ({
    label: description,
    value: tagName,
  })).sort(byField('label'))
);

export const companyCodesForETRByTagNameSelector = createSelector(
  ({ reports }) => reports.effectiveTaxRate.companyCodes,
  ({ user }) => user.user,
  (companyCodes, user) => {
    const filteredCompanyCodes = companyCodes.map(({ companyCode, ctts }) => ({
      label: companyCode,
      value: companyCode,
      ctts,
    }))
      .filter(companyCode => checkAccessBasedOnAssignmentPermissions({
        user,
        withAssignments: [{
          assignmentType: assignmentTypes.CC,
          requiredPermission: assignmentPermissions.Adjust,
        }],
        companyCode: companyCode.value,
      }));

    // eslint-disable-next-line
    return tagName => (tagName, filteredCompanyCodes);
  });

export const hasAnyCompanyCodesForManagingETRDriversSelector = createSelector(
  companyCodesForETRByTagNameSelector,
  companyCodes => tagName => {
    const filteredCompanyCodes = companyCodes(tagName);

    return filteredCompanyCodes && filteredCompanyCodes.length > 0;
  }
);

export const selectCustomETRDrivers = createSelector(
  ({ reports }) => reports.effectiveTaxRate.manageCustomDriversModal.customDrivers,
  customDrivers => customDrivers.map(customDriver => ({
    label: customDriver.description,
    value: customDriver.description,
    ...customDriver,
  })).sort(byField('label'))
);

const formatTaxRate = taxRate => formatValue({ value: taxRate, type: NumeralTypes.PERCENT_4 });
const getLabelForStatutoryTaxRate = (countryTaxType, taxRate) => `${countryTaxType} - ${formatTaxRate(taxRate)}`;
const isSubdivision = (countryTaxType, countryCode) => (countryCode === 'US' && countryTaxType !== 'US2') || (countryCode === 'CA' && countryTaxType !== 'CA1');

export const countryTaxTypesStatutoryTaxRatesSelector = createSelector(
  ({ reports }) => reports.effectiveTaxRate.allCountryTaxTypesAndRates,
  allCountryTaxTypesAndRates => allCountryTaxTypesAndRates
    .filter(({ countryTaxType, countryCode }) => !isSubdivision(countryTaxType, countryCode))
    .map(({ countryTaxType, taxRate, description }) => ({
      label: `${description} - ${formatTaxRate(taxRate)}`,
      value: getLabelForStatutoryTaxRate(countryTaxType, taxRate),
      countryTaxType,
      taxRate,
    }))
);

export const countryTaxTypesSelector = createSelector(
  ({ reports }) => reports.effectiveTaxRate.allCountryTaxTypesAndRates,
  allCountryTaxTypesAndRates => mapCountryTaxTypes(allCountryTaxTypesAndRates)
);

export const reportDataSelector = ({ reports }) => reports.effectiveTaxRate.reportData;

export const detailedCountryTaxTypesDataSelector = ({ reports }) => (
  reports.effectiveTaxRate.allCountryTaxTypesAndRates.map(cttAndRateObj => ({
    ...cttAndRateObj,
    countryTaxTypeWithTaxRate: getLabelForStatutoryTaxRate(
      cttAndRateObj.countryTaxType,
      cttAndRateObj.taxRate
    ),
  }))
);

export const isSavingDataSelector = ({ reports }) => reports.effectiveTaxRate.isSavingData;

export const accountOptionsSelector = createSelector(
  ({ customDescription }) => customDescription,
  ({ accounts }) => accounts,
  (customDescription, accounts) => customDescription
    ? [{ value: customDescription, label: customDescription }, ...accounts]
    : accounts
);

export const accountsSelector = createSelector(
  ({ provision }) => provision.adjustments.accounts,
  accounts => accounts.filter(({ allowAdjustmentFlag }) => allowAdjustmentFlag === YES_FLAG)
    .map(({ label }) => ({ value: label, label }))
);

export const accountsFetchingSelector = ({ provision }) => provision.adjustments.accountsFetching;
export const countryTaxTypesForCompanyCodeSelector = ({ reports }) =>
  reports.effectiveTaxRate.countryTaxTypesForCompanyCode.map(({ countryTaxType, countryCode, description }) => ({
    label: description,
    value: encodeCountryTaxType(countryCode, countryTaxType),
  }));

export const checkHasRightsToModifyRowByCompanyCodeSelector = createSelector(
  ({ user }) => user.user,
  isInCurrentPeriodSelector,
  isFunctionalCurrencySelectedSelector,
  (state, companyCode) => companyCode,
  (user, isInCurrentPeriod, isFunctionalCurrencySelected, companyCode) =>
    isInCurrentPeriod
    && isFunctionalCurrencySelected
    && checkAccessBasedOnAssignmentPermissions({
      user,
      withAssignments: [{
        assignmentType: assignmentTypes.CC,
        requiredPermission: assignmentPermissions.Adjust,
      }],
      companyCode,
    })
);

export const checkHasRightsToModifyRowSelector = createSelector(
  state => state,
  (state, rowData) => rowData,
  (state, rowData) => !rowData
    || (rowData.manual === YES_FLAG && checkHasRightsToModifyRowByCompanyCodeSelector(state, rowData.companyCode))
);

export const checkHasRightsToModifyCTTSelector = createSelector(
  state => state,
  (state, subrows) => subrows,
  (state, subrows) => subrows.reduce((result, row) => result
    ? result
    : checkHasRightsToModifyRowSelector(state, row), false)
);

export const isFetchingGroupTypesSelector = ({ reports }) => reports.effectiveTaxRate.isFetchingGroupTypes;
export const isFetchingGroupItemsSelector = ({ reports }) => reports.effectiveTaxRate.isFetchingGroupItems;
export const isFetchingCountryTaxTypesSelector = ({ reports }) => reports.effectiveTaxRate.isFetchingCountryTaxTypes;
export const isFetchingReportDataSelector = ({ reports }) => reports.effectiveTaxRate.isFetchingReportData;

export const isFetchingDropdownsSelector = createSelector(
  isFetchingGroupTypesSelector,
  isFetchingGroupItemsSelector,
  isFetchingCountryTaxTypesSelector,
  (...fetchingStates) => fetchingStates.some(state => state)
);

export const isFetchingETRDropdownsOrReportDataSelector = createSelector(
  isFetchingDropdownsSelector,
  isFetchingReportDataSelector,
  (...fetchingStates) => fetchingStates.some(state => state)
);

export const effectiveTaxRateDetailsSelector = createSelector(
  ({ reports }) => reports.effectiveTaxRate.selectedCell,
  ({ reports }) => reports.effectiveTaxRate.reportData ? reports.effectiveTaxRate.reportData.rawRecords : null,
  ({ reports }) => reports.effectiveTaxRate.groupBy,
  ({ reports }) => reports.effectiveTaxRate.reportData ? reports.effectiveTaxRate.reportData.columnHeaders: null,
  (selectedCell, rawRecords, groupBy, columnHeaders) => {
    if (!selectedCell) {
      return [];
    }

    const {
      tagName,
      ibtAmount,
      account,
      countryTaxType,
      index,
    } = selectedCell;

    const filteredRecordsForTagName = rawRecords
      .filter(record => record.tagName === tagName)
      .map(record => ({ ...record, percentage: record.amount / ibtAmount }));

    const filteredRecordsForAccount = account
      ? filteredRecordsForTagName.filter(record => record.descr === account)
      : filteredRecordsForTagName;

    const filteredRecordsForCountryTaxType = countryTaxType
      ? filteredRecordsForAccount.filter(record => record.countryTaxType === countryTaxType)
      : filteredRecordsForAccount;

    if (typeof index !== 'undefined' && groupBy && groupBy.value) {
      const item = columnHeaders[index];
      const field = groupingFields[groupBy.value];

      return filteredRecordsForCountryTaxType.filter(record => record[field] === item);
    }

    return filteredRecordsForCountryTaxType;
  }
);
